import { DialogueUIOptionConfig } from "../../components/ui/YouRespondUI";
import { NurseStationComputerUIConfig } from "../../components/ui/NurseStationComputerUI";
import BaseScenarioPart from "../../cvp-webgames-core/Scenario/BaseScenarioPart";
import { Segments } from "../../cvp-webgames-core/Scenario/Segments";
import { constants } from "../../misc/constants";
import { CalmBaseScenario, ScoreType, Window508Type } from "../CalmBaseScenario";
import Scenario1 from "./Scenario1";

export default class Scenario1Part1 extends BaseScenarioPart<CalmBaseScenario> {

	chapterIds = {
		scenario1_1a_s1: 'CALM - Scenario 1 - 1A - Segment 1',
		scenario1_1a_s2: 'CALM - Scenario 1 - 1A - Segment 2',
	}

	private partIndx = 0;

	constructor(scenario: Scenario1, private onDone: () => void) {
		super(scenario);
		CalmBaseScenario.partIndx = this.partIndx;
	}

	public get partId(): string {
		return constants.scenarios.scenario1.parts.level1Part1.id;
	}

	public get videoUrl(): string[] {
		return ['./assets/videos/scenario1/calm-scenario1-avatar' + this.scenario.avatarIndx + '.mp4'];
	}

	public start() {
		const context = this;				
		this.scenario.displayBeginScenarioUI(
			'./assets/images/scenario1/begin-scenario-scenario1.jpg', 
			'calm-scenario1-begin-scenario', 
			() => {
				this.scenario.playAmbientLoop(this.scenario.audioHall);
				context.segment1Step1IntroWalking();
			});	
	}	

	private segment1Step1IntroWalking() {
		this.scenario.destroyAllUIInstances();
		var context = this;
		context.scenario.displayHeader();		
		context.scenario.updateObjective('');
		this.playVideoChapter(this.chapterIds.scenario1_1a_s1, () => { //a.	Intro Charge Nurse walking & Lab Tech phone call - to Response UI
			context.segment1Step2Dialogue();
		})	
	}

	private segment1Step2Dialogue() {
		this.scenario.destroyAllUIInstances();
		var context = this;
		this.scenario.dialog(
			'CN-C03-L1-000',
			[{
				text: 'I’ll find the urinalysis label for you.',
				feedback: 'As the Charge Nurse, it is not your responsibility to track down a missing label since you did not collect the sample. It is subject to reprimand if you label a sample you did not collect.',
				isCorrect: false,
				scoreType: ScoreType.Worst
			}, {
				text: 'Just throw out the sample.',
				feedback: 'Throwing out the urine sample is a common practice when samples are not labeled. However, you still have to resolve which patient needed a urinalysis. ',
				isCorrect: false,
				scoreType: ScoreType.Zero
			}, {
				text: 'I’ll check with staff to identify who collected the sample.',
				isCorrect: true,
				scoreType: ScoreType.Best
			}],
			(selectedOption: DialogueUIOptionConfig) => {
				context.scenario.evaluateAnswer(selectedOption, () => context.segment1Step3FindMiranda(), context.partIndx);
			}
		);
		context.scenario.updateObjective('Select your response to the Lab Tech');				
	}

	private segment1Step3FindMiranda() {
		this.scenario.destroyAllUIInstances();
		var context = this;		
		context.scenario.updateObjective('Resolve the unlabelled urinalysis sample and then return to the Nurse\'s Station');
		context.playVideoChapter(context.chapterIds.scenario1_1a_s2, () => { //a.	Charge Nurse walks to find Miranda, then goes to computer - to Computer Report UI
			context.segment1Step4ShowNurseStationComputer();
			});
	}

	private segment1Step4ShowNurseStationComputer() {
		var context = this;
		context.scenario.updateObjective('');
		context.scenario.update508Window(Window508Type.NurseStationComputerUI);
		context.scenario.gameManager.UiManager.createUIInstance(constants.uiNames.NurseStationComputerUI, {
			reports: [
				{
					title: 'Incident',
					description: 'A urinalysis sample was sent to the lab unlabeled. The staff member who collected the sample went to the lab, identified the specimen, and then labeled it.'
				},
				{
					title: '24 Hour',
					description: 'Nothing to add to report.'
				},
				{
					title: 'End of Shift',
					description: 'Nothing to add to report.'
				}
			],
			review: {
				text: 
				`
				<p class="regular-description-font" style="margin: 0">
					<span style="color: rgb(222, 225, 156)">Charge Nurses are advisors to staff.</span> They have the experience to understand the delicacy 
					and urgency of a given situation and should be able to guide staff accordingly. <br /><br />

					In our scenario, when the lab tech called about an unlabeled urine sample, the Charge 
					Nurse’s first task should have been to <span style="color: rgb(222, 225, 156)">identify the patient who had a doctor’s order for a 
					urinalysis and the RN who collected the sample.</span> <br /><br />

					The Charge Nurse should also recognize that unlabeled samples are normally discarded. 
					However, if a sample is considered an irretrievable specimen, like lumbar puncture or bone 
					marrow, the specimen is not thrown away even if it is unlabeled or mislabeled. In such 
					cases, the Charge Nurse <span style="color: rgb(222, 225, 156)">should identify the RN who acquired the sample and have them 
					work with the lab to verify the patient ID.</span> <br /><br />

					If another specimen has to be collected due to being unlabeled, the Charge Nurse would 
					note this on the <span style="color: rgb(222, 225, 156)">End of Shift report</span> to ensure the sample was re-collected. In contrast, if 
					another specimen has to be collected due to mislabeling, the order may need to be 
					re-ordered.
				</p>
				`
			},
			reflects: [
				{
					title: 'What is the policy at your unit if a sample label and requisition don’t match?',
					answer: ''
				},
				{
					title: 'If another sample could not be collected, how would that affect your approach?',
					answer: ''
				},
				{
					title: 'What are the implications of a mislabeled/unlabeled specimen?',
					answer: ''
				}
			],
			onClosed: (config: NurseStationComputerUIConfig) => {
				context.scenario.update508Window(Window508Type.Main);
				context.partEnd();
			} 
		})
	}

	private partEnd() {
		this.scenario.destroyAllUIInstances();
		var context = this;
		this.scenario.gameManager.customData.saveData.scenarios[this.scenario.currentLevel].badges[context.partIndx].points = CalmBaseScenario.scores[this.partIndx];

		this.scenario.gameManager.customData.saveData.scenarios[this.scenario.currentLevel].badges[context.partIndx].isUnlocked = true;
		this.scenario.gameManager.customData.saveData.scenarios[context.scenario.currentLevel].lastPartId = this.partId;

		this.scenario.gameManager.customData.saveGame(this.scenario.gameManager);

		this.scenario.showBadgingUI(context.partIndx, () => {
			context.onDone();
		});
	}
}